import React, { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box } from '@mui/material';
import { Divider } from '@alycecom/ui';
import { useRouting, useScrollTop } from '@alycecom/hooks';
import { useSendVerificationEmailMutation } from '@alycecom/services';

import StyledHeader from '../StyledHeader/StyledHeader';
import GiftStatusBar from '../../../../components/GiftStatusBar/GiftStatusBar';
import useGiftStatuses from '../../hooks/useGiftStatuses';
import { GIFT_FLOW_CONFIRMATION_ID } from '../../../../constants/giftFlow.constants';
import useResponsive from '../../../../hooks/useResponsive';
import { getProduct, getMeetingNotes } from '../../store/giftFlow/giftFlow.selectors';
import PageTitle from '../PageTitle/PageTitle';
import GiftInfoCard from '../GiftInfoCard/GiftInfoCard';
import {
  getCanBeExchanged,
  getGiftGl,
  getGiftHashId,
  getIsGift,
  getIsRedirectRequired,
} from '../../store/data/data.selectors';
import MeetingInfoCard from '../MeetingInfoCard/MeetingInfoCard';
import Footer from '../../../../components/Shared/Footer';
import ConfirmStepButton from '../../../../components/Shared/ConfirmStepButton';
import { getIsClaimed, getIsLoading } from '../../store/claim/claim.selectors';
import { claimRequest } from '../../store/claim/claim.actions';
import useGoBack from '../../../../hooks/useGoBack';
import FreeMeetingInfoCard from '../MeetingInfoCard/FreeMeetingInfoCard';
import useGiftActions from '../../hooks/useGiftActions';
import usePageLoadEvent from '../../../../hooks/usePageLoadEvent';
import useTrackRecipient from '../../../../hooks/useTrackRecipient';
import useMeetingInfo from '../../hooks/useMeetingInfo';
import { getRecipient } from '../../store/recipient.selectors';
import { getSenderState } from '../../../../store/users/users.selectors';
import { getBranding } from '../../../../store/branding/branding.selectors';
import { getMaxVerificationAttemptReached, getOneTimeCodeVerification } from '../../store/statuses/statuses.selectors';
import { setConfirmedFlow } from '../../store/giftFlow/giftFlow.actions';
import { toggleModal } from '../../store/oneTimeCodeVerification/oneTimeCodeVerification.reducer';

import OneTimeCodeVerificationModal from './OneTimeCodeVerificationModal';
import { ShippingAddressCard, ShippingEmailCard } from './ShippingAddress';
import ResubscribeModal from './ResubscribeModal';
import CallToActionModal from './CallToActionModal';

export interface IConfirmationPageProps {
  parentUrl: string;
}

const ConfirmationPage = ({ parentUrl }: IConfirmationPageProps): JSX.Element => {
  const go = useRouting();
  const dispatch = useDispatch();
  const trackEvent = useTrackRecipient();

  const { statuses, activeStatus, setActiveStatus, previousStepUrl } = useGiftStatuses();
  const { isMobile } = useResponsive();

  const hashId = useSelector(getGiftHashId);
  const sender = useSelector(getSenderState);
  const product = useSelector(getProduct);
  const recipient = useSelector(getRecipient);
  const customStyling = useSelector(getBranding);
  const isGiftClaimed = useSelector(getIsClaimed);
  const isRedirectRequired = useSelector(getIsRedirectRequired);

  const gl = useSelector(getGiftGl);
  const isLoading = useSelector(getIsLoading);
  const isGift = useSelector(getIsGift);
  const meetingNotes = useSelector(getMeetingNotes);
  const { isSingleSlotFlow, isMultiSlotFlow, timezoneName, meetingSlots } = useMeetingInfo();
  const canBeExchanged = useSelector(getCanBeExchanged);
  const isAttemptReached = useSelector(getMaxVerificationAttemptReached);

  const isDonation = product && product.isDonation;

  const [sendVerificationEmail, { isLoading: isVerificationPending }] = useSendVerificationEmailMutation();

  const oneTimeCodeVerification = useSelector(getOneTimeCodeVerification);
  const isVerificationRequired = oneTimeCodeVerification && product?.oneTimeCodeVerification;

  const isSubmitDisabled = isLoading || isVerificationPending;

  const meetingTitle = isSingleSlotFlow
    ? `Scheduled meeting details with ${sender?.firstName}`
    : `Your meeting availability with ${sender?.firstName}`;

  const isAddressRequired = product?.isAddressRequired || !isGift;

  const { handleExchange } = useGiftActions(parentUrl);

  const handleEditAddress = useCallback(() => {
    go(`${parentUrl}/accept/shipping`);
    trackEvent('Address - Clicked change button', { product });
  }, [go, trackEvent, product, parentUrl]);

  const handleEditMeeting = useCallback(() => {
    go(`${parentUrl}/accept/schedule`);
    trackEvent('Meeting - Clicked change button', { product });
  }, [go, trackEvent, product, parentUrl]);

  const handleProductExchange = useCallback(() => {
    handleExchange(isDonation);
    trackEvent('Product - Clicked exchange button', { product });
  }, [handleExchange, isDonation, trackEvent, product]);

  const handleClaim = useCallback(() => {
    if (isVerificationRequired && !isAttemptReached) {
      sendVerificationEmail(hashId).then(() => trackEvent('Email verification — requested'));
    }
    if (isVerificationRequired && isAttemptReached) {
      dispatch(toggleModal(true));
    }
    if (!isVerificationRequired) {
      dispatch(claimRequest(hashId));
    }
    trackEvent('Gift invitation - Clicked claim button', { product, isVerificationRequired, isAttemptReached });
  }, [dispatch, trackEvent, product, isVerificationRequired, hashId, sendVerificationEmail, isAttemptReached]);

  const handleGoBack = useGoBack(previousStepUrl);

  useScrollTop();

  usePageLoadEvent('Confirmation Page - Opened');

  useEffect(() => {
    setActiveStatus(GIFT_FLOW_CONFIRMATION_ID);
  }, [setActiveStatus]);

  useEffect(() => {
    dispatch(setConfirmedFlow(true));
  }, [dispatch]);

  useEffect(() => {
    if (isGiftClaimed && !isRedirectRequired) {
      go(`${parentUrl}/thank-you`);
    }
  }, [go, parentUrl, isRedirectRequired, isGiftClaimed]);

  return (
    <Box bgcolor="common.white" height="100vh">
      <StyledHeader onBackClick={handleGoBack}>
        <GiftStatusBar statuses={statuses} activeStatus={activeStatus} showStatuses={!isMobile} />
      </StyledHeader>
      <Box
        width={1}
        height={['calc(100% - 56px)', 'calc(100% - 80px)']}
        minHeight={['calc(100% - 56px)', 'calc(100% - 80px)']}
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
      >
        <Box>
          <Box mt={[3, 8]} mb={[3, 5]} width={[320, 500]} mx="auto">
            <PageTitle
              title={
                <>
                  An email will be sent to <strong>{recipient?.email}</strong> with all your details
                </>
              }
              subTitle="Confirm your details"
              flipStyles
            />
          </Box>
          <Divider height={2} />
          <Box width={[320, 430]} mx={['auto']} mt={[4, 10]} mb={[12, 5]}>
            {product && sender && (
              <GiftInfoCard
                isLoading={isLoading}
                sender={sender}
                borderColor={customStyling.backgroundColor}
                product={product}
                gl={isDonation ? 'donation value' : gl}
                showDeliverTime={!isDonation}
                onAction={canBeExchanged ? handleProductExchange : undefined}
              />
            )}
            {isAddressRequired && (
              <Box mt={4}>
                {product?.isAddressRequired ? (
                  <ShippingAddressCard
                    isPhysical={product.isAddressRequired}
                    informationCardProps={{ isLoading, onAction: handleEditAddress }}
                  />
                ) : (
                  <ShippingEmailCard isLoading={isLoading} recipient={recipient} onAction={handleEditAddress} />
                )}
              </Box>
            )}
            {isSingleSlotFlow && meetingSlots && sender && (
              <Box mt={4}>
                <MeetingInfoCard
                  isLoading={isLoading}
                  title={meetingTitle}
                  sender={sender}
                  slot={meetingSlots[0]}
                  timezoneName={timezoneName}
                  notes={meetingNotes}
                  showSenderAvatar
                  onAction={handleEditMeeting}
                />
              </Box>
            )}
            {isMultiSlotFlow && meetingSlots && sender && (
              <Box mt={4}>
                <FreeMeetingInfoCard
                  isLoading={isLoading}
                  title={meetingTitle}
                  sender={sender}
                  slots={meetingSlots}
                  timezoneName={timezoneName}
                  notes={meetingNotes}
                  showSenderAvatar
                  onAction={handleEditMeeting}
                />
              </Box>
            )}
          </Box>
        </Box>
        <Box>
          <ConfirmStepButton
            data-testid="confirmDetailsButton"
            width={[1, 430]}
            mb={[0, 4]}
            px={[3, 0]}
            mx={[0, 'auto']}
            text="Submit"
            disabled={isSubmitDisabled}
            onClick={handleClaim}
          />
          <Footer mobileHidden />
        </Box>
      </Box>
      <CallToActionModal parentUrl={parentUrl} />
      <ResubscribeModal />
      <OneTimeCodeVerificationModal />
    </Box>
  );
};

export default ConfirmationPage;
